import type { FieldDefinition } from "../fields/types.js";

/**
 * The one form renderer, shared by the builder's live preview and `apps/public`
 * (docs/04-BUILD-PLAN.md Phase 4: "extract them into packages/shared/src/form-renderer/ now so
 * there is one renderer, not two").
 *
 * Deliberately dependency-free beyond React: `apps/public` has a 150 KB gzipped budget and no
 * component library (CLAUDE.md), so these components take plain className strings and inline
 * brand-token styles rather than importing anything.
 */

export interface RendererBranding {
  primary: string;
  primaryContrast: string;
  accent: string;
  surface: string;
  background: string;
  text: string;
  border: string;
  error: string;
  headingFont?: string;
  bodyFont?: string;
}

export interface RendererSection {
  key: string;
  title?: string | null;
  description?: string | null;
  pageNumber: number;
  position: number;
}

export interface FieldRenderProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Validation message for this field, if any. Wired to the input via aria-describedby. */
  error?: string;
  /** Preview mode renders the control but ignores interaction wiring the builder cannot satisfy. */
  disabled?: boolean;
  branding: RendererBranding;
  /** Options for server-backed types (`ng_state_lga`, `large_list`). */
  lookups?: RendererLookups;
}

export interface RendererLookups {
  states?: { code: string; name: string }[];
  lgasForState?: (stateName: string) => { id: string; name: string }[];
}

/** Width presets for the builder's device toggles. */
export const PREVIEW_WIDTHS = {
  mobile: 390,
  tablet: 768,
  desktop: 1024,
} as const;

export type PreviewWidth = keyof typeof PREVIEW_WIDTHS;
